import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePos } from "../context/PosContext";
import NotificationBell from "./NotificationBell";
import {
  LayoutDashboard, Cake, Boxes, ArrowLeftRight, ShoppingBag,
  Receipt, Wallet, Truck, Users, LogOut, Menu, BarChart3, Store, Building2, Trash2,
  ChefHat, Bike, Snowflake, History, ListTodo, Contact, KeyRound, PiggyBank,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, perm: "dashboard" },
  { to: "/produits", label: "Produits & Marges", icon: Cake, perm: "produits" },
  { to: "/production", label: "Production", icon: ChefHat, perm: "production" },
  { to: "/congelateur", label: "Stock Congélateur", icon: Snowflake, perm: "congelateur" },
  { to: "/stocks", label: "Stock Laboratoire", icon: Boxes, perm: "stocks" },
  { to: "/mouvements", label: "Entrées / Sorties", icon: ArrowLeftRight, perm: "mouvements" },
  { to: "/ventes", label: "Ventes journalières", icon: Receipt, perm: "ventes" },
  { to: "/glovo", label: "Livraison (Glovo)", icon: Bike, perm: "glovo" },
  { to: "/commandes", label: "Commandes clients", icon: ShoppingBag, perm: "commandes" },
  { to: "/destructions", label: "Destruction Produits", icon: Trash2, perm: "destructions" },
  { to: "/depenses", label: "Dépenses", icon: Wallet, perm: "depenses" },
  { to: "/charges-fixes", label: "Charges fixes & Salaires", icon: PiggyBank, perm: "charges" },
  { to: "/rapports", label: "Rapports", icon: BarChart3, perm: "rapports" },
  { to: "/distribution", label: "Distribution B2B", icon: Building2, perm: "distribution" },
  { to: "/crm", label: "CRM B2B", icon: Contact, perm: "crm" },
  { to: "/taches", label: "Tâches à faire", icon: ListTodo, perm: "taches" },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Truck, perm: "fournisseurs" },
  { to: "/points-de-vente", label: "Points de vente", icon: Store, perm: "points_de_vente" },
  { to: "/utilisateurs", label: "Utilisateurs & Permissions", icon: Users, perm: "utilisateurs" },
  { to: "/integrations", label: "API & Intégrations", icon: KeyRound, perm: "parametres" },
  { to: "/activite", label: "Journal d'activité", icon: History, perm: "utilisateurs" },
];

export default function Layout() {
  const { user, logout, isAdmin, can } = useAuth();
  const { salesPosList, selectedPos, setSelectedPos, labPosName } = usePos();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Pages du laboratoire : stock matière propre au labo, pas de sélecteur de point de vente.
  const isLabRoute = ["/stocks", "/mouvements", "/production", "/congelateur"].includes(pathname);
  // Ventes : le point de vente est choisi dans la fenêtre de création, pas dans l'en-tête.
  const hidePosSelector = pathname === "/ventes";

  const items = NAV.filter((n) => can(n.perm, "view"));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <>
      <div className="px-5 py-6 flex items-center gap-3">
        <img src="/logo.png" alt="TIRAMESSI" className="w-12 h-12 rounded-xl object-cover shrink-0" style={{ border: "1px solid var(--tm-border)" }} />
        <div className="leading-none">
          <h1 className="font-display text-2xl tracking-tight">TIRAMESSI</h1>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--tm-gold)] mt-1">The GOAT of tiramisu</p>
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-[rgba(212,175,55,.12)] text-[var(--tm-gold)] font-semibold"
                  : "text-[var(--tm-muted)] hover:text-[var(--tm-beige)] hover:bg-[var(--tm-surface-2)]"
              }`
            }
          >
            <n.icon size={18} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3" style={{ borderTop: "1px solid var(--tm-border)" }}>
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-black shrink-0" style={{ background: "var(--tm-gold)" }}>
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] text-[var(--tm-muted)] capitalize">{user?.role === "admin" ? "Administrateur" : "Employé"}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="tm-btn-danger tm-btn w-full mt-2" data-testid="logout-btn">
          <LogOut size={16} /> Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0" style={{ background: "var(--tm-surface)", borderRight: "1px solid var(--tm-border)" }}>
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="flex flex-col w-72 max-w-[80%] tm-fade" style={{ background: "var(--tm-surface)" }}>
            <SidebarContent />
          </div>
          <div className="flex-1" style={{ background: "rgba(0,0,0,.6)" }} onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Desktop header */}
        <header className="hidden lg:flex items-center justify-between gap-3 px-8 py-3" style={{ borderBottom: "1px solid var(--tm-border)" }}>
          <div className="flex items-center gap-2">
            {isLabRoute ? (
              <>
                <ChefHat size={16} style={{ color: "var(--tm-gold)" }} />
                <span className="text-sm font-semibold" data-testid="lab-fixed-label">{labPosName}</span>
              </>
            ) : hidePosSelector ? null : (
              <>
                <Store size={16} style={{ color: "var(--tm-gold)" }} />
                {isAdmin ? (
                  <select
                    className="tm-input py-1.5 text-sm"
                    style={{ width: "auto", minWidth: 240 }}
                    value={selectedPos}
                    onChange={(e) => setSelectedPos(e.target.value)}
                    data-testid="pos-selector"
                  >
                    <option value="all">Tous les points de vente</option>
                    {salesPosList.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                ) : (
                  <span className="text-sm font-semibold" data-testid="pos-label">{user?.pos_nom || "—"}</span>
                )}
              </>
            )}
          </div>
          <NotificationBell />
        </header>

        {/* Mobile topbar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3" style={{ background: "var(--tm-surface)", borderBottom: "1px solid var(--tm-border)" }}>
          <button onClick={() => setOpen(true)} data-testid="menu-toggle" className="text-[var(--tm-beige)]">
            <Menu size={24} />
          </button>
          <span className="flex items-center gap-2">
            <img src="/logo.png" alt="TIRAMESSI" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-display text-xl tracking-tight">TIRAMESSI</span>
          </span>
          <NotificationBell />
        </header>

        {/* Mobile POS selector */}
        {!hidePosSelector && (
        <div className="lg:hidden px-4 py-2 flex items-center gap-2" style={{ background: "var(--tm-surface)", borderBottom: "1px solid var(--tm-border)" }}>
          {isLabRoute ? (
            <>
              <ChefHat size={15} style={{ color: "var(--tm-gold)" }} />
              <span className="text-sm font-semibold">{labPosName}</span>
            </>
          ) : (
            <>
              <Store size={15} style={{ color: "var(--tm-gold)" }} />
              {isAdmin ? (
                <select className="tm-input py-1.5 text-sm flex-1" value={selectedPos} onChange={(e) => setSelectedPos(e.target.value)}>
                  <option value="all">Tous les points de vente</option>
                  {salesPosList.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
              ) : (
                <span className="text-sm font-semibold">{user?.pos_nom || "—"}</span>
              )}
            </>
          )}
        </div>
        )}

        <main className="flex-1 p-5 sm:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
